import type { QueryClient } from '@tanstack/react-query'
import type { Client } from '@scc/types'
import { authRepository } from '@/data/adapters/api/auth.api'

/** SAME key the `ClientSwitcher` uses, so the guard shares that cache and its
 * `invalidateStoresCache` invalidation. */
export function clientsQueryKey(orgId: string) {
  return ['auth', 'clients', orgId] as const
}

/**
 * Resolves a client by slug for the `/$orgSlug/$clientSlug` route guard.
 *
 * Cache-first for the common case, but **a cache miss is never trusted as
 * proof the client doesn't exist** — it refetches once before giving up.
 *
 * That second step is the fix for a 404-on-a-real-store bug: `ensureQueryData`
 * returns cached data whenever it exists (staleness alone only triggers a
 * background prefetch, and only with `revalidateIfStale`), and
 * `invalidateStoresCache` uses `invalidateQueries`' default
 * `refetchType: 'active'`, which does NOT refetch an *inactive* query — and
 * this list is inactive on the agency overview, where the `ClientSwitcher`
 * isn't mounted. So creating a store and navigating straight to it read a
 * stale list, missed the new slug, and threw `notFound()` for a store that
 * definitely existed. The same staleness also hit stores created in another
 * tab or by a teammate.
 *
 * The fast path is unchanged: a slug already in the cached list costs no
 * request. Only the miss path pays a fetch, and only to avoid a false 404.
 */
export async function resolveClientBySlug(
  queryClient: QueryClient,
  orgId: string,
  slug: string,
): Promise<Client | undefined> {
  const queryKey = clientsQueryKey(orgId)
  const queryFn = () => authRepository.getClients(orgId)

  const cached = await queryClient.ensureQueryData({ queryKey, queryFn })
  const hit = cached.find((c) => c.slug === slug)
  if (hit) return hit

  // Miss — the list may simply be stale. Force one authoritative read (this
  // also refreshes the shared cache, so the switcher stops lagging).
  const fresh = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 })
  return fresh.find((c) => c.slug === slug)
}

/**
 * The server-assigned slug for a store, looked up by id.
 *
 * Client slugs are **not stored** — the API computes them per request across
 * the whole workspace (`assignSlugs` in cc-api's `workspace.routes.ts`), and
 * when two stores share a base slug it suffixes *every* colliding one with its
 * id (`test-client-42`, `test-client-77` — note that neither is `test-client`).
 *
 * So a slug derived locally from just the created store's name is wrong the
 * moment any other store shares that base name, and the resulting URL 404s
 * permanently. Only the server knows the real slug: always resolve it by id
 * after creating a store rather than deriving it.
 */
export async function resolveClientSlugById(
  queryClient: QueryClient,
  orgId: string,
  storeId: string,
): Promise<string | undefined> {
  const fresh = await queryClient.fetchQuery({
    queryKey: clientsQueryKey(orgId),
    queryFn: () => authRepository.getClients(orgId),
    staleTime: 0,
  })
  return fresh.find((c) => c.id === storeId)?.slug
}
